<template>
  <!-- Inline, non-blocking prompt shown inside the chat area while the visitor is
       still anonymous/unverified. It must NEVER block the composer. -->
  <div class="save-prompt">
    <button class="dismiss-btn" :title="t('later')" @click="$emit('dismiss')">&#10005;</button>

    <h4 class="save-title">{{ t('saveChatTitle') }}</h4>
    <p class="save-body">{{ t('saveChatBody') }}</p>

    <input
        v-model="email"
        type="email"
        class="save-input"
        :placeholder="t('email')"
        @keyup.enter="submit"
    />

    <label class="consent-row">
      <input type="checkbox" v-model="consent" />
      <span>{{ t('privacyConsent') }}</span>
    </label>

    <div class="save-actions">
      <button class="primary-btn" :disabled="!canSend || busy" @click="submit">
        {{ t('sendCode') }}
      </button>
      <button class="secondary-btn" @click="$emit('dismiss')">
        {{ t('later') }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from '../i18n.js'

const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/

const props = defineProps({
  busy: { type: Boolean, default: false },
  initialEmail: { type: String, default: '' }
})

const emit = defineEmits(['send-code', 'dismiss'])

const { t } = useI18n()

const email = ref(props.initialEmail)
const consent = ref(false)

const canSend = computed(() => EMAIL_REGEX.test(email.value.trim()) && consent.value)

function submit() {
  if (!canSend.value || props.busy) return
  emit('send-code', email.value.trim())
}
</script>

<style scoped>
.save-prompt {
  position: relative;
  margin: 12px 16px;
  padding: 16px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.dismiss-btn {
  position: absolute;
  top: 8px;
  right: 10px;
  border: none;
  background: none;
  font-size: 16px;
  color: #64748b;
  cursor: pointer;
  line-height: 1;
}
.dismiss-btn:hover { color: #1e3a8a; }

.save-title {
  margin: 0;
  font-size: 15px;
  color: #1e3a8a;
  padding-right: 20px;
}

.save-body {
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  color: #1d4ed8;
}

.save-input {
  padding: 10px;
  border-radius: 6px;
  border: 1px solid #bfdbfe;
  font-family: inherit;
  font-size: 15px;
  background: #fff;
}

.consent-row {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  font-size: 12px;
  color: #1e3a8a;
  line-height: 1.4;
}
.consent-row input[type="checkbox"] {
  margin-top: 2px;
  flex-shrink: 0;
}

.save-actions {
  display: flex;
  gap: 8px;
}

.primary-btn {
  flex: 1;
  padding: 10px;
  background: #2563eb;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  cursor: pointer;
}
.primary-btn:disabled {
  background: #c7c9d1;
  cursor: not-allowed;
}

.secondary-btn {
  padding: 10px 14px;
  background: #ffffff;
  color: #1e3a8a;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  cursor: pointer;
}
.secondary-btn:hover { background: #f0f6ff; }
</style>
